/**
 * @file PriceHistoryChart.tsx
 * @description Accessible inline-SVG line chart for price-per-round (CPR)
 * history, one line per retailer. No third-party charting dependency.
 *
 * Accessibility approach (WCAG 2.2 AA, spec §16):
 *   - The `<svg>` has `role="img"` and `aria-labelledby` pointing at a
 *     `<title>` + `<desc>` element inside the SVG, giving assistive
 *     technologies a semantic name and summary.
 *   - A full data table is rendered below the chart (not just visually
 *     hidden) as the primary accessible alternative. Screen-reader users
 *     can navigate the table directly. The table is wrapped in a
 *     `<details>/<summary>` so sighted users can expand it on demand.
 *   - The chart is `aria-hidden="false"` by default; the accompanying
 *     table is always visible (not `aria-hidden`), so the data is always
 *     in the accessibility tree.
 *   - Each series uses a distinct colour AND a unique `stroke-dasharray`
 *     so low-vision / colour-blind users can tell lines apart.
 *   - Data points render as `<circle>` elements with `<title>` tooltips.
 *
 * Chart rendering uses a step-function forward-fill: because the backend
 * only records a snapshot when price/stock changes, each change point
 * represents "the value held until the next change". The chart therefore
 * draws a horizontal segment from each point to the next before dropping
 * to the new value, faithfully representing the step-function nature of
 * the data (see docs/design/price-trends.md §"How it powers features").
 */

import { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { PriceHistorySeries } from '../../services/productsApi';

// ─── Colour + dash palette (8 series max) ────────────────────────────────────
// Each entry is [stroke, dasharray]. Using both colour and dash pattern
// means the chart remains usable in greyscale / for colour-blind users.
// Tuned to read clearly against the dark tactical surface.
const SERIES_STYLES: ReadonlyArray<[string, string]> = [
  ['#5cc8ff', 'none'],
  ['#ff6b6b', '8 4'],
  ['#4cd784', '4 4'],
  ['#ffb74d', '10 3 2 3'],
  ['#c08bff', '6 2'],
  ['#3df0e0', '2 2'],
  ['#e0a878', '12 4 2 4'],
  ['#aab3bf', '4 2 2 2'],
];

const SVG_WIDTH = 600;
const SVG_HEIGHT = 260;
// left pad widened (#117) so the y-tick values clear the rotated axis label.
const PAD = { top: 16, right: 24, bottom: 48, left: 68 };
const CHART_W = SVG_WIDTH - PAD.left - PAD.right;
const CHART_H = SVG_HEIGHT - PAD.top - PAD.bottom;
const TICK_COUNT = 5;
const DATE_TICK_COUNT = 5;

interface ChartPoint {
  t: number; // epoch ms
  cpr: number;
}

interface ParsedSeries {
  name: string;
  points: ChartPoint[];
  style: [string, string];
}

function parseSeries(series: PriceHistorySeries[]): ParsedSeries[] {
  return series
    .map((s, idx) => {
      const points: ChartPoint[] = s.points
        .map((p) => {
          const cpr = p.pricePerRound === null ? null : Number(p.pricePerRound);
          const t = new Date(p.observedAt).getTime();
          if (cpr === null || !Number.isFinite(cpr) || !Number.isFinite(t)) return null;
          return { t, cpr } as ChartPoint;
        })
        .filter((p): p is ChartPoint => p !== null)
        .sort((a, b) => a.t - b.t);
      return {
        name: s.retailerName,
        points,
        style: SERIES_STYLES[idx % SERIES_STYLES.length] as [string, string],
      };
    })
    .filter((s) => s.points.length > 0);
}

/** Build a step-function polyline path: horizontal then vertical drop. */
function buildStepPath(
  points: ChartPoint[],
  scaleT: (t: number) => number,
  scaleCpr: (cpr: number) => number
): string {
  if (points.length === 0) return '';

  const parts: string[] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const x = scaleT(p.t);
    const y = scaleCpr(p.cpr);
    if (i === 0) {
      parts.push(`M ${x},${y}`);
    } else {
      // Horizontal to new x at previous y, then drop
      const prevY = scaleCpr(points[i - 1]!.cpr);
      parts.push(`H ${x}`);
      if (prevY !== y) parts.push(`V ${y}`);
    }
  }
  // Extend the last segment to the right edge so the chart reads "still valid"
  const lastX = scaleT(points[points.length - 1]!.t);
  const rightEdge = scaleT(
    points[points.length - 1]!.t +
      (points.length > 1 ? points[points.length - 1]!.t - points[points.length - 2]!.t : 0)
  );
  if (rightEdge > lastX) parts.push(`H ${Math.min(rightEdge, CHART_W)}`);

  return parts.join(' ');
}

function formatDateShort(t: number): string {
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCpr(cpr: number): string {
  return `$${cpr.toFixed(3)}/rd`;
}

// Y-axis ticks omit the "/rd" suffix — the axis label already reads "Price per
// round", so the unit would be redundant and made the labels wide enough to
// collide with the rotated axis label (#117).
function formatCprTick(cpr: number): string {
  return `$${cpr.toFixed(3)}`;
}

function formatDateFull(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ─── SVG chart ───────────────────────────────────────────────────────────────

interface SvgChartProps {
  series: ParsedSeries[];
  minT: number;
  maxT: number;
  minCpr: number;
  maxCpr: number;
  productName: string;
}

function SvgChart({ series, minT, maxT, minCpr, maxCpr, productName }: SvgChartProps) {
  const theme = useTheme();
  const gridColor = theme.palette.tactical.line;
  const axisColor = theme.palette.tactical.lineHi;
  const labelColor = theme.palette.tactical.dim;
  const tRange = maxT - minT || 1;
  const cprRange = maxCpr - minCpr || 1;

  const scaleT = (t: number) => PAD.left + ((t - minT) / tRange) * CHART_W;
  const scaleCpr = (cpr: number) => PAD.top + CHART_H - ((cpr - minCpr) / cprRange) * CHART_H;

  // Y-axis ticks
  const yTicks = Array.from({ length: TICK_COUNT }, (_, i) => {
    const v = minCpr + (cprRange / (TICK_COUNT - 1)) * i;
    return { v, y: scaleCpr(v) };
  });

  // X-axis ticks
  const xTicks = Array.from({ length: DATE_TICK_COUNT }, (_, i) => {
    const t = minT + (tRange / (DATE_TICK_COUNT - 1)) * i;
    return { t, x: scaleT(t) };
  });

  const summaryText =
    series.length === 1
      ? `Price history for ${productName} from ${formatDateShort(minT)} to ${formatDateShort(maxT)}, showing ${series[0]!.name}.`
      : `Price history for ${productName} from ${formatDateShort(minT)} to ${formatDateShort(maxT)}, showing ${series.length} retailers.`;

  const descId = 'phc-desc';
  const titleId = 'phc-title';

  return (
    <svg
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      role="img"
      aria-labelledby={`${titleId} ${descId}`}
      style={{ width: '100%', maxWidth: SVG_WIDTH, display: 'block' }}
      data-testid="price-history-svg"
    >
      <title id={titleId}>Price history chart</title>
      <desc id={descId}>{summaryText}</desc>

      {/* Grid lines */}
      {yTicks.map(({ v, y }) => (
        <line
          key={v}
          x1={PAD.left}
          x2={PAD.left + CHART_W}
          y1={y}
          y2={y}
          stroke={gridColor}
          strokeWidth={1}
        />
      ))}

      {/* Y axis */}
      <line
        x1={PAD.left}
        x2={PAD.left}
        y1={PAD.top}
        y2={PAD.top + CHART_H}
        stroke={axisColor}
        strokeWidth={1}
      />

      {/* X axis */}
      <line
        x1={PAD.left}
        x2={PAD.left + CHART_W}
        y1={PAD.top + CHART_H}
        y2={PAD.top + CHART_H}
        stroke={axisColor}
        strokeWidth={1}
      />

      {/* Y-axis tick labels */}
      {yTicks.map(({ v, y }) => (
        <text
          key={v}
          x={PAD.left - 6}
          y={y}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={10}
          fill={labelColor}
        >
          {formatCprTick(v)}
        </text>
      ))}

      {/* X-axis tick labels */}
      {xTicks.map(({ t, x }) => (
        <text
          key={t}
          x={x}
          y={PAD.top + CHART_H + 14}
          textAnchor="middle"
          fontSize={10}
          fill={labelColor}
        >
          {formatDateShort(t)}
        </text>
      ))}

      {/* Y-axis label — pinned to the far-left edge, clear of the tick values. */}
      <text
        x={14}
        y={PAD.top + CHART_H / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={11}
        fill={labelColor}
        transform={`rotate(-90, 14, ${PAD.top + CHART_H / 2})`}
      >
        Price per round
      </text>

      {/* Series lines + points */}
      {series.map((s) => {
        const [stroke, dasharray] = s.style;
        const path = buildStepPath(s.points, scaleT, scaleCpr);
        return (
          <g key={s.name}>
            <path
              d={path}
              fill="none"
              stroke={stroke}
              strokeWidth={2}
              strokeDasharray={dasharray === 'none' ? undefined : dasharray}
            />
            {s.points.map((p, pi) => (
              <circle key={pi} cx={scaleT(p.t)} cy={scaleCpr(p.cpr)} r={4} fill={stroke}>
                <title>
                  {s.name}: {formatCpr(p.cpr)} on {formatDateShort(p.t)}
                </title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Legend ──────────────────────────────────────────────────────────────────

function ChartLegend({ series }: { series: ParsedSeries[] }) {
  if (series.length === 0) return null;
  return (
    <Box
      component="ul"
      aria-label="Chart legend"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        p: 0,
        m: 0,
        listStyle: 'none',
        mt: 1,
      }}
    >
      {series.map((s) => {
        const [stroke] = s.style;
        return (
          <Box component="li" key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: 'inline-block',
                width: 24,
                height: 3,
                backgroundColor: stroke,
                borderRadius: 1,
              }}
            />
            <Typography variant="caption">{s.name}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Data table fallback ─────────────────────────────────────────────────────

function DataTable({ rawSeries }: { rawSeries: PriceHistorySeries[] }) {
  return (
    <Box component="details" sx={{ mt: 2 }}>
      <Box component="summary" sx={{ cursor: 'pointer', userSelect: 'none' }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          View price history data table
        </Typography>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small" aria-label="Price history data" data-testid="price-history-table">
          <TableHead>
            <TableRow>
              <TableCell>Retailer</TableCell>
              <TableCell>Date observed</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Price per round</TableCell>
              <TableCell>Stock status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rawSeries.flatMap((s) =>
              s.points.map((p, pi) => (
                <TableRow key={`${s.productListingId}-${pi}`}>
                  <TableCell>{s.retailerName}</TableCell>
                  <TableCell>{formatDateFull(p.observedAt)}</TableCell>
                  <TableCell>
                    {p.price === null
                      ? '—'
                      : new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: p.currency || 'USD',
                        }).format(Number(p.price))}
                  </TableCell>
                  <TableCell>
                    {p.pricePerRound === null ? '—' : formatCpr(Number(p.pricePerRound))}
                  </TableCell>
                  <TableCell>{p.stockStatus}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export interface PriceHistoryChartProps {
  /** Raw series array from the price-history endpoint. */
  series: PriceHistorySeries[];
  /** Human-readable product name used in the SVG accessible description. */
  productName: string;
}

/**
 * Renders a step-function CPR trend chart for one product across multiple
 * retailers. Uses a dependency-free inline SVG so no extra bundle weight is
 * added.
 *
 * Accessibility:
 *   - SVG has `role="img"` + `<title>` + `<desc>` (WCAG 1.1.1, 4.1.2).
 *   - Data table fallback is always in the DOM inside a `<details>` element.
 *   - Legend uses `<ul>` with `aria-label`.
 *
 * Usage:
 *   ```tsx
 *   <PriceHistoryChart series={data.series} productName="Federal HST 9mm 124gr JHP" />
 *   ```
 */
export function PriceHistoryChart({ series, productName }: PriceHistoryChartProps) {
  const parsed = useMemo(() => parseSeries(series), [series]);

  if (parsed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="price-history-empty">
        No price history yet. Snapshots are captured when prices or stock change; check back after
        the next scrape.
      </Typography>
    );
  }

  const allPoints = parsed.flatMap((s) => s.points);
  const minT = Math.min(...allPoints.map((p) => p.t));
  const maxT = Math.max(...allPoints.map((p) => p.t));
  const allCprs = allPoints.map((p) => p.cpr);
  // Pad the CPR range slightly so lines don't touch the chart edges
  const cprMin = Math.min(...allCprs);
  const cprMax = Math.max(...allCprs);
  const cprPad = (cprMax - cprMin) * 0.1 || 0.01;
  const minCpr = cprMin - cprPad;
  const maxCpr = cprMax + cprPad;

  return (
    <Box>
      <SvgChart
        series={parsed}
        minT={minT}
        maxT={maxT}
        minCpr={minCpr}
        maxCpr={maxCpr}
        productName={productName}
      />
      <ChartLegend series={parsed} />
      <DataTable rawSeries={series} />
    </Box>
  );
}

export default PriceHistoryChart;
